import React, {
  KeyboardEvent,
  PointerEvent,
  SyntheticEvent,
  useCallback,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { General } from 'icons/untitledui';
import isVideoAttachment from 'shiftNotes/helpers/isVideoAttachment';
import { usePosthogCapture } from 'shiftNotes/posthogCapture/shiftNotes';
import * as Styled from 'shiftNotes/styles/NotesStack.style';
import type { Attachment } from 'shiftNotes/_types';

import * as Buttons from 'theme/components/Buttons.style';

import ResizeHandleIcon from 'shiftNotes/assets/resizeHandle.svg?react';

interface AttachmentModalProps {
  noteId: string;
  attachment: Attachment;
  open: boolean;
  onClose: () => void;
  onDelete?: () => void;
}

interface ResizeFrame {
  cardRect: DOMRect;
  chromeWidth: number;
  chromeHeight: number;
  mediaWidth: number;
  maxMediaWidth: number;
}

interface ResizeDrag {
  frame: ResizeFrame;
  grabOffsetX: number;
  grabOffsetY: number;
  didResize: boolean;
}

const MIN_MEDIA_WIDTH_PX = 240;
const KEYBOARD_RESIZE_STEP = 0.1;
const RESIZE_KEY_DIRECTIONS: Partial<Record<string, 1 | -1>> = {
  ArrowUp: 1,
  ArrowRight: 1,
  ArrowDown: -1,
  ArrowLeft: -1,
};

const clampMediaWidth = (width: number, frame: ResizeFrame): number =>
  Math.min(Math.max(width, MIN_MEDIA_WIDTH_PX), frame.maxMediaWidth);

export default function AttachmentModal({
  noteId,
  attachment,
  open,
  onClose,
  onDelete,
}: AttachmentModalProps): JSX.Element {
  const { t } = useTranslation('pageShiftNotes');
  const capture = usePosthogCapture();
  const isVideo = isVideoAttachment(attachment);
  const cardRef = useRef<HTMLDivElement>(null);
  const mediaRef = useRef<HTMLElement | null>(null);
  const resizeDragRef = useRef<ResizeDrag | null>(null);
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);
  const [isVideoLoading, setIsVideoLoading] = useState(isVideo);
  const [customMediaWidth, setCustomMediaWidth] = useState<number | null>(null);

  const setMediaElement = useCallback((element: HTMLElement | null) => {
    mediaRef.current = element;
  }, []);

  const handleVideoMetadata = (event: SyntheticEvent<HTMLVideoElement>) => {
    const { videoWidth, videoHeight } = event.currentTarget;
    if (videoWidth && videoHeight) setAspectRatio(videoWidth / videoHeight);
  };

  const handleImageLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    if (naturalWidth && naturalHeight) {
      setAspectRatio(naturalWidth / naturalHeight);
    }
  };

  const measureResizeFrame = (): ResizeFrame | null => {
    const card = cardRef.current;
    const media = mediaRef.current;
    if (!card || !media || !aspectRatio) return null;
    const cardRect = card.getBoundingClientRect();
    const mediaRect = media.getBoundingClientRect();
    const chromeWidth = cardRect.width - mediaRect.width;
    const chromeHeight = cardRect.height - mediaRect.height;
    const viewportMargins = 2 * Styled.ATTACHMENT_MODAL_VIEWPORT_MARGIN_PX;
    return {
      cardRect,
      chromeWidth,
      chromeHeight,
      mediaWidth: mediaRect.width,
      maxMediaWidth: Math.min(
        window.innerWidth - viewportMargins - chromeWidth,
        (window.innerHeight - viewportMargins - chromeHeight) * aspectRatio
      ),
    };
  };

  const handleResizePointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    const frame = measureResizeFrame();
    if (!frame) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    resizeDragRef.current = {
      frame,
      grabOffsetX: event.clientX - frame.cardRect.right,
      grabOffsetY: event.clientY - frame.cardRect.bottom,
      didResize: false,
    };
  };

  const handleResizePointerMove = (event: PointerEvent<HTMLButtonElement>) => {
    const drag = resizeDragRef.current;
    if (!drag || !aspectRatio) return;
    const { frame } = drag;
    const centerX = frame.cardRect.left + frame.cardRect.width / 2;
    const centerY = frame.cardRect.top + frame.cardRect.height / 2;
    const cornerOffsetX =
      event.clientX - drag.grabOffsetX - centerX - frame.chromeWidth / 2;
    const cornerOffsetY =
      event.clientY - drag.grabOffsetY - centerY - frame.chromeHeight / 2;
    const cornerStepX = 1 / 2;
    const cornerStepY = 1 / (2 * aspectRatio);
    const width =
      (cornerOffsetX * cornerStepX + cornerOffsetY * cornerStepY) /
      (cornerStepX ** 2 + cornerStepY ** 2);
    drag.didResize = true;
    setCustomMediaWidth(clampMediaWidth(width, frame));
  };

  const handleResizePointerEnd = () => {
    const drag = resizeDragRef.current;
    resizeDragRef.current = null;
    if (!drag?.didResize) return;
    capture.noteAttachmentResized({
      shift_note_id: noteId,
      input_method: 'pointer',
    });
  };

  const handleResizeKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const direction = RESIZE_KEY_DIRECTIONS[event.key];
    const frame = direction ? measureResizeFrame() : null;
    if (!direction || !frame) return;
    event.preventDefault();
    setCustomMediaWidth(
      clampMediaWidth(
        frame.mediaWidth * (1 + direction * KEYBOARD_RESIZE_STEP),
        frame
      )
    );
    capture.noteAttachmentResized({
      shift_note_id: noteId,
      input_method: 'keyboard',
    });
  };

  return (
    <Styled.AttachmentModalDialog
      open={open}
      onClose={onClose}
      maxWidth={false}
    >
      <Styled.AttachmentModalCard ref={cardRef}>
        <Styled.AttachmentModalButtons>
          {onDelete && (
            <Buttons.SecondaryLightIcon $size="small" onClick={onDelete}>
              <General.Trash01 />
              {t('note.Delete')}
            </Buttons.SecondaryLightIcon>
          )}
          <Styled.AttachmentModalCloseButton
            $size="small"
            onClick={onClose}
            aria-label={t('note.closeAttachment')}
          >
            <General.XClose />
          </Styled.AttachmentModalCloseButton>
        </Styled.AttachmentModalButtons>
        {isVideo ? (
          <Styled.AttachmentModalVideo
            ref={setMediaElement}
            src={attachment.file}
            controls
            autoPlay
            playsInline
            $aspectRatio={aspectRatio}
            $customWidth={customMediaWidth}
            $isLoading={isVideoLoading}
            onLoadedMetadata={handleVideoMetadata}
            onLoadedData={() => setIsVideoLoading(false)}
            onError={() => setIsVideoLoading(false)}
          />
        ) : (
          <Styled.AttachmentModalImage
            ref={setMediaElement}
            src={attachment.file}
            $customWidth={customMediaWidth}
            onLoad={handleImageLoad}
          />
        )}
        {aspectRatio !== null && (
          <Styled.AttachmentModalResizeHandle
            type="button"
            aria-label={t('note.resizeAttachment')}
            onPointerDown={handleResizePointerDown}
            onPointerMove={handleResizePointerMove}
            onPointerUp={handleResizePointerEnd}
            onPointerCancel={handleResizePointerEnd}
            onKeyDown={handleResizeKeyDown}
          >
            <ResizeHandleIcon aria-hidden />
          </Styled.AttachmentModalResizeHandle>
        )}
      </Styled.AttachmentModalCard>
    </Styled.AttachmentModalDialog>
  );
}
